import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import type { GridRowId } from "@mui/x-data-grid"

import type {
  DeviceRowsByInstallationStatus,
  DeviceWGroupsNStates,
  InstalledOnLoading,
} from "types/device.types"
import type { DeviceGroup } from "types/group.types"

import { useRemoveDevicesFromGroupMutation } from "features/api"
import { selectCurrentOrgId } from "features/store/orgSlice"
import { buildGetErrorMessage, snackbarMutation } from "helpers/utils/mutations"
import usePermissions from "helpers/hooks/usePermissions"
import RemoveDevicePopup from "widgets/deviceGroups/editor/RemoveDevicePopup"
import DevicesTableToolbar from "./DevicesTableToolbar"
import DevicesTableToolbarSelect from "./DevicesTableToolbarSelect"
import DevicesTable from "."

interface DeviceGroupDevicesTableProps {
  devices: DeviceRowsByInstallationStatus
  installedOnLoading: InstalledOnLoading
  statusesLoading: boolean
  isLoading: boolean
  titleKey: string
  group: DeviceGroup
}

function DeviceGroupDevicesTable({
  devices,
  installedOnLoading,
  statusesLoading,
  isLoading,
  titleKey,
  group,
}: DeviceGroupDevicesTableProps) {
  const { t } = useTranslation()
  const { isEditor } = usePermissions()
  const orgId = useSelector(selectCurrentOrgId)
  const [removeDevicesFromGroup, { isLoading: isRemoving }] =
    useRemoveDevicesFromGroupMutation()
  const [selectedDevices, setSelectedDevices] = useState<DeviceWGroupsNStates[]>([])
  const [selectedIds, setSelectedIds] = useState<GridRowId[]>([])
  const [openPopup, setOpenPopup] = useState<boolean>(false)

  const handleDelete = useCallback(
    async (deviceIds: number[]) =>
      snackbarMutation({
        mutation: removeDevicesFromGroup({
          lessIds: deviceIds,
          groupId: group.id,
          orgId: Number(orgId),
        }).unwrap(),
        getErrorMessage: buildGetErrorMessage(
          t("error.DELETING_ITEM", { item: t("generic.DEVICES") }),
        ),
        getSuccessMessage: () =>
          t("success.REMOVING_ITEM", {
            item: t("generic.DEVICES"),
            count: 2,
            context: "male",
          }),
      }),
    [group.id, orgId, removeDevicesFromGroup, t],
  )

  const handleOpen = (selectedDevices: DeviceWGroupsNStates[]) => {
    setOpenPopup(true)
    setSelectedDevices(selectedDevices)
  }

  const handleClose = () => {
    setOpenPopup(false)
    setSelectedIds([])
  }

  const onConfirmRemove = useCallback(() => {
    const lessIds = selectedDevices.map((selectedDevice) => selectedDevice.device.less_id)
    handleDelete(lessIds)
      .then(() => handleClose())
      .catch(() => {})
  }, [handleDelete, selectedDevices])

  return (
    <>
      <DevicesTable
        titleKey={titleKey}
        devices={devices}
        installedOnLoading={installedOnLoading}
        statusesLoading={statusesLoading}
        isWaiting={isLoading}
        fileNameEntity={`group_${group.name}`}
        checkboxSelection={isEditor}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        toolbarActions={isEditor && <DevicesTableToolbar devices={devices.all} />}
        selectionActions={
          <DevicesTableToolbarSelect
            handleOpen={() =>
              handleOpen(
                devices.all.filter(({ device }) => selectedIds.includes(device.less_id)),
              )
            }
          />
        }
      />
      <RemoveDevicePopup
        isLoading={isRemoving}
        selectedDevices={selectedDevices}
        deviceGroup={group}
        open={openPopup}
        onClose={handleClose}
        onConfirm={onConfirmRemove}
      />
    </>
  )
}

export default DeviceGroupDevicesTable
